import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';
import { useAuth } from './AuthContext';

/**
 * Staff capability policy — client side.
 *
 * Mirrors `GET /api/permissions/me` (see server/routes/permissionRoutes.js):
 * an org-scoped, narrowing-only policy that applies to every NARROWABLE staff
 * role — `manager`, `user`, `apartment_manager`. `admin` is never narrowed,
 * and `field_supervisor` is not narrowable either: its live persona is the
 * mobile app, its office realm is dormant, and `/api/permissions` is
 * deliberately NOT on its server fence, so fetching it for that role would
 * 403 on every page load.
 *
 * This context is COSMETIC. The server (`middleware/auth.js`) is the sole
 * authority on what a request may do — this only decides what the SPA shows.
 * `can(key)` therefore defaults to TRUE whenever it cannot be sure (no data
 * yet, still loading, or the fetch failed): hiding a link because a network
 * request failed would be worse than showing a link that then 403s.
 *
 * No `localStorage` caching, no TTL: a revocation must take effect on the
 * very next load, and CLAUDE.md already documents the class of bug where a
 * TTL-cached settings copy served a stale value long after an admin changed
 * it. Re-fetches on mount / on role or org change, and exposes `refresh()`
 * so the admin editor can push a fresh copy right after saving.
 *
 * `ready` is the companion of that fail-open `can()`: it says the answer for
 * THIS user is settled (a non-narrowable role needs no fetch, a narrowable
 * role's fetch has come back or failed). Rendering must NOT wait for it —
 * fail-open is the right bias for showing a link. A caller that would
 * otherwise SPEND A REQUEST on a capability-governed endpoint must, though:
 * firing while `can()` is merely optimistic produces a guaranteed 403 on every
 * page load for a role whose capability was revoked.
 *
 * `usePermissionsOptional()` is for components that must render both inside
 * and outside a `PermissionsProvider` — e.g. a section reused by tests or
 * hosts that don't wrap it in the provider. Unlike `usePermissions()` (which
 * keeps throwing, and is still the right hook for any provider-guaranteed
 * call site), it never throws: with no provider above it, it returns a
 * fail-open stand-in (`can()` ⇒ true, `ready` ⇒ true) for exactly the reason
 * this whole policy already fails open — it is COSMETIC, the server
 * (`middleware/auth.js`) is the sole authority, and hiding UI because a
 * provider (or its fetch) was absent would be worse than showing an action
 * that then 403s.
 */

// Mirrors server/utils/managerPermissions.js#NARROWABLE_ROLES (= the server's
// CREATABLE_ROLES): the only roles this policy ever narrows. This client copy
// is COSMETIC — the server is the sole authority, and drift here only ever
// costs a wasted or skipped GET, never a security decision.
const NARROWABLE_ROLES = ['manager', 'user', 'apartment_manager'];

const PermissionsContext = createContext(null);

export const usePermissions = () => {
    const context = useContext(PermissionsContext);
    if (!context) {
        throw new Error('usePermissions must be used within a PermissionsProvider');
    }
    return context;
};

// Frozen so it is referentially stable across renders (no provider means no
// re-render would recreate it anyway, but this also stops any consumer from
// mutating the shared fallback) and module-level so every call site outside a
// provider shares the exact same object.
const FAIL_OPEN_PERMISSIONS = Object.freeze({
    capabilities: null,
    loading: false,
    error: null,
    ready: true,
    refresh: () => {},
    can: () => true,
    grants: [],
    hasGrant: () => false,
    hasRoleOrGrant: () => false
});

export const usePermissionsOptional = () => useContext(PermissionsContext) || FAIL_OPEN_PERMISSIONS;

export const PermissionsProvider = ({ children }) => {
    const { user } = useAuth();
    // `null` capabilities means "no restrictions known" — which `can()` below
    // already treats as all-true, so a non-narrowable role never needs an
    // actual literal all-true object built client-side (this client has no
    // copy of the capability catalog outside the admin editor's fetches).
    const [capabilities, setCapabilities] = useState(null);
    // The caller's own PER-USER extra permissions ("הרשאות נוספות",
    // server/utils/userGrants.js) — only ever non-empty for role `user`. These
    // WIDEN what the SPA shows, so unlike `can()` they fail CLOSED: a failed or
    // pending read means no extra screens (the server enforces either way).
    const [grants, setGrants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Starts FALSE even for a role that needs no fetch: the provider's own
    // effect has not run yet on the first render, and a consumer effect (which
    // React runs child-first, i.e. BEFORE this provider's) would otherwise read
    // a `ready` that only accidentally means "settled".
    const [ready, setReady] = useState(false);

    const role = user?.role || null;
    const organization = user?.organization || null;

    const refresh = useCallback(async () => {
        // Every role in NARROWABLE_ROLES fetches its effective map. `admin` is
        // never narrowed. `field_supervisor` is not narrowable at all — its
        // office realm is dormant and `/api/permissions` is deliberately not on
        // its server fence, so calling it for that role would 403 on every page
        // load — so both short-circuit here with no request.
        if (!NARROWABLE_ROLES.includes(role)) {
            setCapabilities(null);
            setGrants([]);
            setError(null);
            setLoading(false);
            setReady(true);
            return;
        }

        // Un-settle for the duration of the fetch: on a role/org change (or an
        // editor-driven re-read) the previous answer describes a different
        // policy, and a caller gating a request on it must wait for the new one.
        setReady(false);
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/permissions/me');
            setCapabilities(response.data?.capabilities || null);
            setGrants(Array.isArray(response.data?.grants) ? response.data.grants : []);
        } catch (err) {
            setError(err);
            setCapabilities(null);
            setGrants([]);
        } finally {
            setLoading(false);
            // Settled either way — a FAILED read is an answer too, and `can()`
            // fails open on it, so gated callers proceed exactly as they did
            // before this policy existed rather than hanging forever.
            setReady(true);
        }
    }, [role, organization]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const can = useCallback((key) => {
        // Missing map, still loading, or a failed fetch: fail open. Only an
        // explicit `false` for a known key narrows anything.
        if (!capabilities || loading || error) return true;
        if (!(key in capabilities)) return true;
        return capabilities[key] !== false;
    }, [capabilities, loading, error]);

    const hasGrant = useCallback(
        (key) => role === 'user' && grants.includes(key),
        [role, grants]
    );

    // "Is this screen/action open to me?" for a gate that used to be a role
    // list: the role passes as before, and a `user` passes with any of the
    // given grants. Mirrors `requireRoleOrGrant` in server/middleware/auth.js.
    const hasRoleOrGrant = useCallback((roles, grantKeys) => {
        if (role && Array.isArray(roles) && roles.includes(role)) return true;
        const keys = Array.isArray(grantKeys) ? grantKeys : (grantKeys ? [grantKeys] : []);
        return keys.some((k) => hasGrant(k));
    }, [role, hasGrant]);

    const value = {
        capabilities,
        grants,
        hasGrant,
        hasRoleOrGrant,
        loading,
        error,
        ready,
        refresh,
        can
    };

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
};
